import React, { useMemo, useState } from 'react';
import styles from './Attachment.module.scss';
import { IFile } from '../../../types';
import { nebulaChatApi } from '../../../redux/services/nebulaChat';
import { ImageAttachment } from './children/ImageAttachment';
import { AttachmentNameDisplay, GenericAttachment } from './children/GenerticAttachment';
import { AudioAttachment, ChatAudioAttachment } from './children/AudioAttachment';
import { VideoAttachment } from './children/VideoAttachment';
import { PdfAttachment } from './children/PdfAttachment';
import AriaButton from '../../../components/AriaComponents/AriaButton';
import Icon from '../../../components/Icon';
import { AnimatedDiv } from '../../../animation';

export { formatBytes } from './children/GenerticAttachment';

export type DisplayFile = {
    meta: 'chat';
    file: IFile;
} | {
    meta: 'input';
    file: File;
};

// ── Container ─────────────────────────────────────────────────────────────────

interface AttachmentContainerProps {
    isLarge?: boolean;
    isDelete?: boolean;
    actionButton?: React.ReactNode;
    children: (showOptions: boolean) => React.ReactNode;
}

const AttachmentContainer: React.FC<AttachmentContainerProps> = ({
    isLarge,
    isDelete,
    actionButton,
    children,
}) => {
    const [showOptions, setShowOptions] = useState(false);

    return (
        <div
            className={styles.attachment_container}
            data-is-large={isLarge || null}
            onMouseEnter={() => setShowOptions(true)}
            onMouseLeave={() => setShowOptions(false)}
            onFocus={() => setShowOptions(true)}
            onBlur={() => setShowOptions(false)}
            onClick={e => e.stopPropagation()}
            data-comp='attachment'
        >
            {children(showOptions)}
            {actionButton && (
                <div
                    className={styles.primary_action_container}
                    data-is-download={isDelete ? 'false' : 'true'}
                >
                    <AnimatedDiv
                        visible={showOptions}
                        inAnimation={isDelete ? ['fade'] : ['fade', 'slideFromBottom']}
                    >
                        {actionButton}
                    </AnimatedDiv>
                </div>
            )}
        </div>
    );
};

// ── Input attachment (local file, pre-send) ───────────────────────────────────

interface InputAttachmentProps {
    file: File;
    mini?: boolean;
    handleFileDelete?: () => void;
}

const InputAttachment: React.FC<InputAttachmentProps> = ({ file, mini, handleFileDelete }) => {
    const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);

    const deleteButton = handleFileDelete ? (
        <AriaButton onClick={handleFileDelete}>
            <Icon name='cross' />
        </AriaButton>
    ) : undefined;

    switch (file.type) {
        case 'audio':
        case 'audio/ogg':
        case 'audio/wav':
        case 'audio/mpeg':
        case 'audio/x-m4a':
        case 'audio/aac':
            return (
                <AttachmentContainer isDelete actionButton={deleteButton}>
                    {() => <AudioAttachment initialFile={file} />}
                </AttachmentContainer>
            );
        case 'image/webp':
        case 'image/png':
        case 'image/img':
        case 'image/jpeg':
        case 'image/gif':
        case 'image/sticker+png':
        case 'image/svg+xml':
            return (
                <AttachmentContainer isLarge={!mini} isDelete actionButton={deleteButton}>
                    {(showOptions) => (
                        <>
                            {mini ? (
                                <div className={styles.preview_container_mini}>
                                    <img src={objectUrl} alt={file.name} />
                                </div>
                            ) : (
                                <div className={styles.preview_container}>
                                    <img src={objectUrl} alt={file.name} />
                                </div>
                            )}
                            <AnimatedDiv
                                visible={mini || showOptions}
                                className={styles.file_content_container}
                                inAnimation={['fade', 'slideFromBottom']}
                                transition={{ duration: 0.4 }}
                            >
                                <AttachmentNameDisplay file={file} />
                            </AnimatedDiv>
                        </>
                    )}
                </AttachmentContainer>
            );
        default:
            return (
                <AttachmentContainer isDelete actionButton={deleteButton}>
                    {() => <GenericAttachment file={{ name: file.name, content_type: file.type, size: file.size }} />}
                </AttachmentContainer>
            );
    }
};

// ── Chat attachment (server file) ─────────────────────────────────────────────

interface ChatAttachmentProps {
    file: IFile;
    mini?: boolean;
}

const ChatAttachment: React.FC<ChatAttachmentProps> = ({ file, mini }) => {
    const [fetchForDownload] = nebulaChatApi.endpoints.pullFileN.useLazyQuery();

    const downloadFile = async () => {
        const result = await fetchForDownload({ file_uuid: file.uuid, thumbnail: false });
        if (!result.data) return;
        const a = document.createElement('a');
        a.href = result.data;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const downloadButton = (
        <AriaButton className={styles.download_button} onClick={downloadFile}>
            <Icon name='arrowDownWithLine' />
        </AriaButton>
    );

    switch (file.content_type) {
        case 'audio':
        case 'audio/ogg':
        case 'audio/wav':
        case 'audio/mpeg':
        case 'audio/x-m4a':
        case 'audio/aac':
            return (
                <AttachmentContainer actionButton={downloadButton}>
                    {() => <ChatAudioAttachment file={file} />}
                </AttachmentContainer>
            );
        case 'image/webp':
        case 'image/png':
        case 'image/img':
        case 'image/jpeg':
        case 'image/gif':
        case 'image/sticker+png':
        case 'image/svg+xml':
            // Large images carry their own download button inside the overlay
            return (
                <AttachmentContainer isLarge={!mini} actionButton={mini ? downloadButton : undefined}>
                    {(showOptions) => (
                        <ImageAttachment
                            file={file}
                            mini={mini || false}
                            showOptions={showOptions}
                            onDownload={downloadFile}
                        />
                    )}
                </AttachmentContainer>
            );
        case 'video/mp4':
        case 'video/quicktime':
        case 'video/x-msvideo':
        case 'video/x-ms-wmv':
        case 'video/webm':
            return (
                <AttachmentContainer actionButton={downloadButton}>
                    {(showOptions) => (
                        <VideoAttachment file={file} showOptions={showOptions} />
                    )}
                </AttachmentContainer>
            );
        case 'application/pdf':
            return (
                <AttachmentContainer actionButton={downloadButton}>
                    {() => <PdfAttachment file={file} />}
                </AttachmentContainer>
            );
        default:
            return (
                <AttachmentContainer actionButton={downloadButton}>
                    {() => <GenericAttachment file={file} />}
                </AttachmentContainer>
            );
    }
};

// ── Public entry point ────────────────────────────────────────────────────────

interface Props {
    attachment: DisplayFile;
    mini?: boolean;
    handleFileDelete?: () => void;
}

const Attachment: React.FC<Props> = ({ attachment, mini, handleFileDelete }) => {
    if (attachment.meta === 'input') {
        return <InputAttachment file={attachment.file} mini={mini} handleFileDelete={handleFileDelete} />;
    }
    return <ChatAttachment file={attachment.file} mini={mini} />;
};

export default Attachment;
